import { DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { ensure, isFilled } from "lopermut"
import { useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { KnowledgeBaseItemKind, KnowledgeBaseItemSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { useCreateKnowledgeBaseMutation } from "./useCreateKnowledgeBaseMutation"
import { useDeleteKnowledgeBaseMutation } from "./useDeleteKnowledgeBaseMutation"
import { useKnowledgeBaseQuery } from "./useKnowledgeBaseQuery"
import { useRetrieveWebpageKnowledgeBaseMutation } from "./useRetrieveWebpageKnowledgeBaseMutation"
import { useSortKnowledgeBaseMutation } from "./useSortKnowledgeBaseMutation"
import { useUpdateKnowledgeBaseMutation } from "./useUpdateKnowledgeBaseMutation"

type KnowledgeBaseItem = z.infer<typeof KnowledgeBaseItemSchema>

export type DraftItem = {
  id?: string
  originalDraftId?: string
  kind: KnowledgeBaseItemKind
  content: string | FileList | null
  title: string | null
  order: number
  campaignId: string | null
  hasChangeLog?: boolean
  retrievedAt: Date | null
  fetchFailedAt: Date | null
  isRetrieving: boolean | null
  websiteRefreshFrequency: KnowledgeBaseItem["websiteRefreshFrequency"]
}

let draftIdCounter = 0

function generateDraftId() {
  return `draft-${Date.now()}-${++draftIdCounter}`
}

function isDraft(id: string | undefined): boolean {
  return !!id && id.startsWith("draft-")
}

function validateDraftItem(item: DraftItem): boolean {
  switch (item.kind) {
    case "Text":
      return typeof item.content === "string" && isFilled(item.content.trim())
    case "QuestionAndAnswer":
      return (
        isFilled(item.title) && typeof item.content === "string" && isFilled(item.content.trim())
      )
    case "File":
      return item.content instanceof FileList && item.content.length > 0
    case "Website": {
      return isFilled(item.title)
    }
  }
}

function serverItemToDraft(item: KnowledgeBaseItem, originalDraftId?: string): DraftItem {
  return {
    id: item.id.toString(),
    originalDraftId,
    kind: item.kind,
    content: item.content,
    title: item.title ?? null,
    order: item.order,
    campaignId: item.campaignId ?? null,
    hasChangeLog: item.hasChangeLog,
    retrievedAt: item.retrievedAt ? new Date(item.retrievedAt) : null,
    fetchFailedAt: item.fetchFailedAt ? new Date(item.fetchFailedAt) : null,
    isRetrieving: item.isRetrieving,
    websiteRefreshFrequency: item.websiteRefreshFrequency,
  }
}

export function useKnowledgeBaseForm(kind: KnowledgeBaseItemKind, campaignId: string | null) {
  const { t } = useTranslation()
  const previousKindRef = useRef(kind)

  // Single source of truth: merged items array (server items + local drafts)
  // Local drafts are stored separately and merged on-demand
  const [localDrafts, setLocalDrafts] = useState<Map<string, Partial<DraftItem>>>(new Map())
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set())

  const updateDraft = useCallback((id: string, updates: Partial<DraftItem>) => {
    setLocalDrafts((prev) => {
      const newMap = new Map(prev)
      const existing = newMap.get(id) || {}
      newMap.set(id, { ...existing, ...updates })
      return newMap
    })
  }, [])

  // Sync server data to local drafts when query refetches
  // This ensures isRetrieving state stays in sync for refetchInterval to work
  const syncServerDataToDrafts = useCallback((serverItems: KnowledgeBaseItem[]) => {
    setLocalDrafts((prev) => {
      const newMap = new Map(prev)
      let hasChanges = false

      serverItems.forEach((item) => {
        const itemId = item.id.toString()
        const existingDraft = prev.get(itemId)

        // Keep the UI in sync with website fetch lifecycle changes coming from the backend.
        const serverIsRetrieving = item.isRetrieving ?? false
        const serverRetrievedAt = item.retrievedAt ? new Date(item.retrievedAt) : null
        const serverFetchFailedAt = item.fetchFailedAt ? new Date(item.fetchFailedAt) : null
        const draftIsRetrieving = existingDraft?.isRetrieving ?? false
        const draftRetrievedAt = existingDraft?.retrievedAt ?? null
        const draftFetchFailedAt = existingDraft?.fetchFailedAt ?? null
        const serverContent = item.content ?? null
        const serverWebsiteRefreshFrequency = item.websiteRefreshFrequency ?? null
        const draftWebsiteRefreshFrequency = existingDraft?.websiteRefreshFrequency ?? null

        if (
          serverIsRetrieving !== draftIsRetrieving ||
          serverRetrievedAt?.getTime() !== draftRetrievedAt?.getTime() ||
          serverFetchFailedAt?.getTime() !== draftFetchFailedAt?.getTime() ||
          serverWebsiteRefreshFrequency !== draftWebsiteRefreshFrequency
        ) {
          const existing = newMap.get(itemId) || {}
          newMap.set(itemId, {
            ...existing,
            isRetrieving: item.isRetrieving,
            retrievedAt: serverRetrievedAt,
            fetchFailedAt: serverFetchFailedAt,
            content: serverContent,
            websiteRefreshFrequency: serverWebsiteRefreshFrequency,
          })
          hasChanges = true
        }
      })

      return hasChanges ? newMap : prev
    })
  }, [])

  const { data: knowledgeBase, isPending } = useKnowledgeBaseQuery({
    query: { kind, campaignId },
    onSuccess: (response) => {
      syncServerDataToDrafts(response.data)
    },
  })

  // Clear local drafts when kind changes
  if (previousKindRef.current !== kind && localDrafts.size > 0) {
    setLocalDrafts(new Map())
    previousKindRef.current = kind
  } else {
    previousKindRef.current = kind
  }

  // Derive merged items: server items + local draft overrides
  const items = useMemo(() => {
    const serverItems = (knowledgeBase ?? []).sort((a, b) => a.order - b.order)
    const serverItemIds = new Set(serverItems.map((item) => item.id.toString()))
    const result: DraftItem[] = []

    // Add all server items with draft overrides
    serverItems.forEach((item) => {
      const itemId = item.id.toString()
      const draft = localDrafts.get(itemId)
      const serverDraft = serverItemToDraft(item, draft?.originalDraftId)
      result.push(draft ? { ...serverDraft, ...draft } : serverDraft)
    })

    // Add items from localDrafts that aren't in serverItems yet (transitional state)
    // This prevents items from disappearing during draft-to-persisted transition
    localDrafts.forEach((draft, id) => {
      if (!isDraft(id) && !serverItemIds.has(id)) {
        // Server ID exists in localDrafts but not in knowledgeBase yet
        // Include it to prevent UI jump during query refetch
        result.push({ ...draft, id } as DraftItem)
      } else if (isDraft(id)) {
        // Pure draft items (not yet persisted)
        result.push({ ...draft, id } as DraftItem)
      }
    })

    return result.sort((a, b) => a.order - b.order)
  }, [knowledgeBase, localDrafts])

  const { mutateAsync: createKnowledgeBase } = useCreateKnowledgeBaseMutation()
  const { mutateAsync: updateKnowledgeBase } = useUpdateKnowledgeBaseMutation()
  const { mutateAsync: deleteKnowledgeBase } = useDeleteKnowledgeBaseMutation()
  const { mutateAsync: sortKnowledgeBase } = useSortKnowledgeBaseMutation()
  const { mutateAsync: retrieveWebpageKnowledgeBase } = useRetrieveWebpageKnowledgeBaseMutation()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const removeDraft = useCallback((id: string) => {
    setLocalDrafts((prev) => {
      const newMap = new Map(prev)
      newMap.delete(id)
      return newMap
    })
  }, [])

  const setSaving = useCallback((id: string, saving: boolean) => {
    setSavingIds((prev) => {
      const newSet = new Set(prev)
      if (saving) {
        newSet.add(id)
      } else {
        newSet.delete(id)
      }
      return newSet
    })
  }, [])

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) return

      const oldIndex = items.findIndex((item) => item.id === active.id)
      const newIndex = items.findIndex((item) => item.id === over.id)

      if (oldIndex === -1 || newIndex === -1) return

      const reorderedItems = [...items]
      const [movedItem] = reorderedItems.splice(oldIndex, 1)
      if (!movedItem) return
      reorderedItems.splice(newIndex, 0, movedItem)

      // Update orders optimistically
      reorderedItems.forEach((item, index) => {
        if (item.id) {
          updateDraft(item.id, { order: index })
        }
      })

      // Persist to server
      const persistedItems = reorderedItems
        .filter((item) => item.id && !isDraft(item.id))
        .map((item, index) => ({
          id: ensure(item.id),
          order: index,
        }))

      if (persistedItems.length > 0) {
        await sortKnowledgeBase({ values: persistedItems })
        toast.success(t("knowledge.form.updated"))
      }
    },
    [items, sortKnowledgeBase, t, updateDraft],
  )

  const onDelete = useCallback(
    async (index: number) => {
      const item = items[index]
      if (!item?.id) return

      const itemId = item.id

      if (isDraft(itemId)) {
        removeDraft(itemId)
      } else {
        removeDraft(itemId)
        await deleteKnowledgeBase({ id: itemId })
        toast.success(t("knowledge.form.deleted"))
      }
    },
    [items, removeDraft, deleteKnowledgeBase, t],
  )

  const onUpdate = useCallback(
    async (
      index: number,
      { hideToast = false, updates }: { hideToast?: boolean; updates?: Partial<DraftItem> } = {},
    ) => {
      let item = items[index]

      // Apply updates if provided (to handle async state issue)
      if (updates && item) {
        item = { ...item, ...updates }
      } else if (item?.id) {
        // Also check for pending updates in localDrafts
        const pendingUpdates = localDrafts.get(item.id)
        if (pendingUpdates) {
          item = { ...item, ...pendingUpdates }
        }
      }

      if (!item?.id) return

      const itemId = item.id

      if (item.campaignId && !campaignId) {
        throw new Error(
          "An item with a campaign ID cannot be updated without being on the campaign detail",
        )
      }

      setSaving(itemId, true)

      try {
        if (isDraft(itemId)) {
          // Validate draft before persisting - if invalid, just don't persist it
          if (!validateDraftItem(item)) {
            setSaving(itemId, false)
            return
          }

          // Create new item
          const newEntry = await createKnowledgeBase({
            kind: item.kind,
            content: item.content,
            order: item.order,
            title: item.title ?? undefined,
            campaignId: campaignId ?? item.campaignId ?? undefined,
          })

          // Preserve originalDraftId to use as stable React key
          const originalDraftId = item.originalDraftId ?? itemId
          setLocalDrafts((prev) => {
            const newMap = new Map(prev)
            const draftData = newMap.get(itemId)
            newMap.delete(itemId)
            // Map server ID to preserve originalDraftId for stable React key
            newMap.set(newEntry.id.toString(), {
              ...draftData,
              id: newEntry.id.toString(),
              originalDraftId,
              hasChangeLog: newEntry.hasChangeLog,
              retrievedAt: newEntry.retrievedAt ? new Date(newEntry.retrievedAt) : null,
              fetchFailedAt: newEntry.fetchFailedAt ? new Date(newEntry.fetchFailedAt) : null,
              isRetrieving: newEntry.isRetrieving,
              websiteRefreshFrequency: newEntry.websiteRefreshFrequency,
            })
            return newMap
          })

          if (!hideToast) {
            toast.success(t("knowledge.form.created"))
          }
        } else {
          // Update existing item
          const existingItem = knowledgeBase?.find((i) => i.id.toString() === itemId)
          const shouldUpdate =
            existingItem?.content !== item.content ||
            existingItem?.title !== item.title ||
            existingItem?.order !== item.order

          if (!shouldUpdate) return

          const updatedItem = await updateKnowledgeBase({
            id: itemId,
            content: item.content,
            title: item.title ?? undefined,
          })

          // Update optimistic state with server response
          updateDraft(itemId, {
            hasChangeLog: updatedItem.hasChangeLog,
            retrievedAt: updatedItem.retrievedAt ? new Date(updatedItem.retrievedAt) : null,
            fetchFailedAt: updatedItem.fetchFailedAt ? new Date(updatedItem.fetchFailedAt) : null,
            isRetrieving: updatedItem.isRetrieving,
            websiteRefreshFrequency: updatedItem.websiteRefreshFrequency,
          })

          if (!hideToast) {
            toast.success(t("knowledge.form.updated"))
          }
        }
      } finally {
        setSaving(itemId, false)
      }
    },
    [
      items,
      campaignId,
      setSaving,
      localDrafts,
      createKnowledgeBase,
      t,
      knowledgeBase,
      updateKnowledgeBase,
      updateDraft,
    ],
  )

  const onAppend = useCallback(
    (fileList?: FileList) => {
      const nextOrder = items.length
      const draftId = generateDraftId()

      const baseDraft = {
        originalDraftId: draftId,
        kind,
        order: nextOrder,
        campaignId: campaignId ?? null,
        hasChangeLog: false,
        retrievedAt: null,
        fetchFailedAt: null,
        isRetrieving: null,
        websiteRefreshFrequency: null,
      }

      switch (kind) {
        case "Text":
        case "Website": {
          updateDraft(draftId, { ...baseDraft, content: "", title: null })
          return draftId
        }

        case "QuestionAndAnswer": {
          updateDraft(draftId, { ...baseDraft, content: "", title: "" })
          return draftId
        }

        case "File": {
          updateDraft(draftId, {
            ...baseDraft,
            content: fileList ?? null,
            title: fileList?.[0]?.name ?? null,
          })
          return draftId
        }
      }
    },
    [items.length, kind, campaignId, updateDraft],
  )

  const onDuplicate = useCallback(
    async (index: number) => {
      const currentItem = items[index]
      if (!currentItem) return

      const duplicateId = generateDraftId()
      const newOrder = index + 1

      updateDraft(duplicateId, {
        ...currentItem,
        id: duplicateId,
        originalDraftId: duplicateId,
        order: newOrder,
        campaignId: campaignId ?? currentItem.campaignId,
        hasChangeLog: false,
      })

      // Update orders for items after the duplicate
      items.forEach((item, i) => {
        if (item.id && i > index) {
          updateDraft(item.id, { order: i + 1 })
        }
      })

      if (currentItem.content) {
        // Save the duplicate
        const duplicateIndex = items.findIndex((item) => item.id === duplicateId)
        if (duplicateIndex !== -1) {
          await onUpdate(duplicateIndex)

          // Persist order changes
          const persistedItems = items
            .filter((item) => item.id && !isDraft(item.id))
            .map((item, i) => ({
              id: ensure(item.id),
              order: i,
            }))

          if (persistedItems.length > 0) {
            await sortKnowledgeBase({ values: persistedItems })
          }
        }
      }
    },
    [items, campaignId, updateDraft, onUpdate, sortKnowledgeBase],
  )

  const onRetrieveWebpage = useCallback(
    async (itemId: string) => {
      await retrieveWebpageKnowledgeBase(itemId)
      updateDraft(itemId, {
        isRetrieving: true,
        fetchFailedAt: null,
      })
    },
    [retrieveWebpageKnowledgeBase, updateDraft],
  )

  const fields = items.map((item, index) => ({
    ...item,
    id: item.id ?? `item-${index}`,
    originalDraftId: item.originalDraftId ?? item.id ?? `item-${index}`,
  }))

  return {
    items,
    fields,
    sensors,
    isPending,
    handleDragEnd,
    onDelete,
    onUpdate,
    onAppend,
    onDuplicate,
    onRetrieveWebpage,
    getItem: (id: string) => items.find((item) => item.id === id) ?? null,
    updateItem: (id: string, updates: Partial<DraftItem>) => {
      updateDraft(id, updates)
    },
    isSaving: (id: string) => savingIds.has(id),
  }
}
